/** RoyalEngagement — Ring Ceremony Section */

import React from 'react';
import { motion } from 'framer-motion';
import { Gem, Calendar, Clock, MapPin } from 'lucide-react';
import { ROYAL_EASE } from '../theme';
import type { RoyalContent } from '../content';

export function CeremonySection({
  content,
}: {
  content: RoyalContent['ceremony'];
}) {
  return (
    <section id="ceremony" className="relative w-full px-5 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-32 bg-[var(--royal-ivory-dark)] text-[var(--royal-ink)]">
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: ROYAL_EASE }}
          className="flex flex-col gap-4 items-center text-center"
        >
          <span className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-[var(--royal-maroon)]">
            <span className="h-px w-6 bg-[var(--royal-gold)]" />
            {content.label}
          </span>
          <h2 className="font-serif text-[clamp(2rem,5vw,3.4rem)] font-semibold leading-[1.08] text-[var(--royal-ink)]">
            {content.title}
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-2 lg:items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: ROYAL_EASE }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl border-2 border-[var(--royal-gold)]/30 shadow-2xl">
              <img
                src={content.image}
                alt={content.title}
                className="w-full h-auto object-cover aspect-[4/3]"
              />
              {/* Gold overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--royal-maroon)]/20 to-transparent" />
            </div>
            {/* Decorative corners */}
            <div className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-[var(--royal-gold)]" />
            <div className="absolute -top-4 -right-4 w-8 h-8 border-t-2 border-r-2 border-[var(--royal-gold)]" />
            <div className="absolute -bottom-4 -left-4 w-8 h-8 border-b-2 border-l-2 border-[var(--royal-gold)]" />
            <div className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-[var(--royal-gold)]" />
          </motion.div>

          {/* Content side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.2, ease: ROYAL_EASE }}
            className="flex flex-col gap-6"
          >
            <div className="flex items-center gap-3 text-[var(--royal-gold)]">
              <Gem className="w-6 h-6" />
              <span className="font-serif text-lg italic">The Exchange of Rings</span>
            </div>

            <p className="text-lg leading-relaxed text-[var(--royal-muted)]">
              {content.description}
            </p>

            <div className="grid gap-4 mt-4">
              <div className="flex items-center gap-4 rounded-xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-4">
                <Calendar className="w-5 h-5 text-[var(--royal-maroon)]" />
                <div>
                  <p className="text-sm text-[var(--royal-muted)]">Date</p>
                  <p className="font-medium text-[var(--royal-ink)]">{content.date}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-4">
                <Clock className="w-5 h-5 text-[var(--royal-maroon)]" />
                <div>
                  <p className="text-sm text-[var(--royal-muted)]">Time</p>
                  <p className="font-medium text-[var(--royal-ink)]">{content.time}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-[var(--royal-border)] bg-[var(--royal-ivory)] p-4">
                <MapPin className="w-5 h-5 text-[var(--royal-maroon)]" />
                <div>
                  <p className="text-sm text-[var(--royal-muted)]">Venue</p>
                  <p className="font-medium text-[var(--royal-ink)]">{content.venue}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
